import { useCallback, useMemo, useState, type ChangeEvent, type ReactElement } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, RefreshCw, ScrollText, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  apiClient,
  type LoggerLevelDto,
  type SystemLoggersDto,
  type SystemLoggersInstanceDto,
  type SystemLoggersServiceDto,
} from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shell/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const LOGGERS_QUERY_KEY = ['system', 'loggers'] as const;
const STALE_TIME_MS = 30_000;

const LEVEL_CHOICES: readonly string[] = ['INHERIT', 'OFF', 'ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'];
const INHERIT_SENTINEL = 'INHERIT';

const DEFAULT_TAIL_BYTES = 64 * 1024;
const MAX_TAIL_BYTES = 1024 * 1024;
const AUTO_REFRESH_INTERVAL_MS = 5_000;

function formatRelative(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const deltaSec = Math.floor((Date.now() - ms) / 1000);
  if (deltaSec < 60) return `${String(deltaSec)}s ago`;
  if (deltaSec < 3600) return `${String(Math.floor(deltaSec / 60))}m ago`;
  if (deltaSec < 86_400) return `${String(Math.floor(deltaSec / 3600))}h ago`;
  return `${String(Math.floor(deltaSec / 86_400))}d ago`;
}

export function AdminLogsPage(): ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'levels' | 'lines'>('levels');

  const query = useQuery({
    queryKey: LOGGERS_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.getSystemLoggers({ signal }),
    staleTime: STALE_TIME_MS,
  });

  const data: SystemLoggersDto | undefined = query.data;
  const aggregatedAgo = data !== undefined ? formatRelative(data.aggregated_at) : null;

  const handleRefresh = useCallback(() => {
    query.refetch().catch(() => undefined);
  }, [query]);

  return (
    <>
      <PageHeader
        icon={<ScrollText className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.logs.heading')}
        subtitle={
          aggregatedAgo !== null
            ? t('routes.admin.logs.aggregatedAt', { ago: aggregatedAgo })
            : t('routes.admin.logs.subheading')
        }
        actions={
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={query.isFetching}>
            <RefreshCw
              className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            {t('routes.admin.logs.refresh')}
          </Button>
        }
      />
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as 'levels' | 'lines');
        }}
      >
        <TabsList aria-label={t('routes.admin.logs.heading')}>
          <TabsTrigger value="levels" icon={<ScrollText className="h-4 w-4" aria-hidden="true" />}>
            {t('routes.admin.logs.tabs.levels')}
          </TabsTrigger>
          <TabsTrigger value="lines" icon={<FileText className="h-4 w-4" aria-hidden="true" />}>
            {t('routes.admin.logs.tabs.lines')}
          </TabsTrigger>
        </TabsList>
        <div className="px-8 py-5 space-y-5 bg-background">
          {query.isPending && <Skeletons />}
          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.logs.error.heading')}
                message={t('routes.admin.logs.error.message')}
                codeLabel={t('routes.admin.users.error.codeLabel')}
                onRetry={handleRefresh}
                retryLabel={t('routes.admin.logs.error.retry')}
              />
            </div>
          )}
          {query.isSuccess && (
            <>
              <TabsContent value="levels">
                <LevelsTab services={data?.services ?? []} />
              </TabsContent>
              <TabsContent value="lines">
                <LinesTab services={data?.services ?? []} />
              </TabsContent>
            </>
          )}
        </div>
      </Tabs>
    </>
  );
}

// ---- Levels tab ---------------------------------------------------------------------

function LevelsTab({ services }: { readonly services: readonly SystemLoggersServiceDto[] }): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>('');
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const isOpen = (serviceId: string): boolean => openMap[serviceId] ?? true;

  const setAllOpen = (open: boolean): void => {
    setOpenMap(Object.fromEntries(services.map((s) => [s.service_id, open])));
  };

  const toggleOne = (serviceId: string): void => {
    setOpenMap((prev) => ({ ...prev, [serviceId]: !(prev[serviceId] ?? true) }));
  };

  const resetAll = useMutation({
    mutationFn: async () => {
      for (const service of services) {
        const reachable = service.instances.find((i) => i.reachable);
        if (reachable === undefined) continue;
        const targets = Object.entries(reachable.loggers).filter(
          ([, lvl]) => lvl.configured_level !== null,
        );
        for (const [logger] of targets) {
          await apiClient.setLoggerLevelOnService(service.service_id, logger, { level: null });
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGGERS_QUERY_KEY });
    },
  });

  const handleResetAll = (): void => {
    if (!window.confirm(t('routes.admin.logs.levels.resetAllConfirm'))) return;
    resetAll.mutate();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            value={filter}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFilter(e.target.value);
            }}
            className="pl-8"
            placeholder=""
            aria-label="Filter loggers"
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setAllOpen(true);
          }}
        >
          {t('routes.admin.logs.levels.expandAll')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setAllOpen(false);
          }}
        >
          {t('routes.admin.logs.levels.minimizeAll')}
        </Button>
        <Button size="sm" variant="outline" onClick={handleResetAll} disabled={resetAll.isPending}>
          {t('routes.admin.logs.levels.resetAll')}
        </Button>
      </div>
      {services.map((service) => (
        <ServiceLevelsCard
          key={service.service_id}
          service={service}
          filter={filter}
          open={isOpen(service.service_id)}
          onToggle={() => {
            toggleOne(service.service_id);
          }}
        />
      ))}
    </div>
  );
}

interface ServiceLevelsCardProps {
  readonly service: SystemLoggersServiceDto;
  readonly filter: string;
  readonly open: boolean;
  readonly onToggle: () => void;
}

function ServiceLevelsCard({ service, filter, open, onToggle }: ServiceLevelsCardProps): ReactElement {
  const { t } = useTranslation();
  const reachableInstances = service.instances.filter((i) => i.reachable);
  const totalLoggers = reachableInstances[0]?.loggers
    ? Object.keys(reachableInstances[0].loggers).length
    : 0;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-semibold tracking-tight">
            {service.service_id.toLowerCase()}
          </CardTitle>
          <CardDescription className="mt-1 text-xs">
            {t('routes.admin.logs.levels.loggerCount', { count: totalLoggers })}
            {' · '}
            {service.instances.length} {service.instances.length === 1 ? 'instance' : 'instances'}
          </CardDescription>
        </div>
        <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={open}>
          {open ? '▾' : '▸'}
        </Button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0 space-y-4">
          {service.instances.map((instance) => (
            <InstanceLevelsTable
              key={instance.instance_id ?? instance.host ?? '?'}
              serviceId={service.service_id}
              instance={instance}
              filter={filter}
              multipleInstances={service.instances.length > 1}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

interface InstanceLevelsTableProps {
  readonly serviceId: string;
  readonly instance: SystemLoggersInstanceDto;
  readonly filter: string;
  readonly multipleInstances: boolean;
}

function InstanceLevelsTable({
  serviceId,
  instance,
  filter,
  multipleInstances,
}: InstanceLevelsTableProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const setLevel = useMutation({
    mutationFn: async ({ logger, level }: { readonly logger: string; readonly level: string | null }) => {
      if (instance.instance_id === null) return;
      await apiClient.setLoggerLevel(serviceId, instance.instance_id, logger, { level });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGGERS_QUERY_KEY });
    },
  });

  const setLevelOnAll = useMutation({
    mutationFn: async ({ logger, level }: { readonly logger: string; readonly level: string | null }) => {
      await apiClient.setLoggerLevelOnService(serviceId, logger, { level });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGGERS_QUERY_KEY });
    },
  });

  const entries = useMemo(() => {
    if (!instance.reachable) return [] as readonly (readonly [string, LoggerLevelDto])[];
    const f = filter.trim().toLowerCase();
    return Object.entries(instance.loggers)
      .filter(([name]) => f === '' || name.toLowerCase().includes(f))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [instance.loggers, instance.reachable, filter]);

  if (!instance.reachable) {
    return (
      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <Badge variant="secondary" className="mr-2 font-normal">
          {instance.instance_id ?? '—'}
        </Badge>
        {t('routes.admin.logs.levels.instanceUnreachable')}
        {instance.error !== undefined && instance.error !== '' ? ` — ${instance.error}` : ''}
      </div>
    );
  }

  return (
    <div>
      {multipleInstances && (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary" className="font-normal">
            {instance.instance_id ?? '—'}
          </Badge>
          <span>{instance.status ?? ''}</span>
        </div>
      )}
      <div className="max-h-[260px] overflow-auto rounded border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-background">
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">{t('routes.admin.logs.levels.table.logger')}</th>
              <th className="px-3 py-2 font-medium">{t('routes.admin.logs.levels.table.configured')}</th>
              <th className="px-3 py-2 font-medium">{t('routes.admin.logs.levels.table.effective')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {entries.map(([name, lvl]) => (
              <LoggerRow
                key={name}
                logger={name}
                level={lvl}
                availableLevels={instance.available_levels}
                disabled={setLevel.isPending || setLevelOnAll.isPending}
                onChange={(level) => {
                  setLevel.mutate({ logger: name, level });
                }}
                onApplyAll={
                  multipleInstances
                    ? (level) => {
                        setLevelOnAll.mutate({ logger: name, level });
                      }
                    : null
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface LoggerRowProps {
  readonly logger: string;
  readonly level: LoggerLevelDto;
  readonly availableLevels: readonly string[];
  readonly disabled: boolean;
  readonly onChange: (level: string | null) => void;
  readonly onApplyAll: ((level: string | null) => void) | null;
}

function LoggerRow({
  logger,
  level,
  availableLevels,
  disabled,
  onChange,
  onApplyAll,
}: LoggerRowProps): ReactElement {
  const { t } = useTranslation();
  const choices = availableLevels.length > 0 ? availableLevels : LEVEL_CHOICES.filter((l) => l !== INHERIT_SENTINEL);
  const current = level.configured_level ?? INHERIT_SENTINEL;

  function handleChange(value: string): void {
    onChange(value === INHERIT_SENTINEL ? null : value);
  }

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-3 py-1.5 font-mono text-[11px]">{logger}</td>
      <td className="px-3 py-1.5">
        <select
          value={current}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            handleChange(e.target.value);
          }}
          className="rounded border border-border bg-background px-1.5 py-0.5 text-[11px]"
        >
          <option value={INHERIT_SENTINEL}>{t('routes.admin.logs.levels.inherited')}</option>
          {choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-1.5 text-muted-foreground">{level.effective_level ?? '—'}</td>
      <td className="px-3 py-1.5">
        {onApplyAll !== null && (
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              onApplyAll(current === INHERIT_SENTINEL ? null : current);
            }}
            className="text-[11px]"
          >
            {t('routes.admin.logs.levels.applyAll')}
          </Button>
        )}
      </td>
    </tr>
  );
}

// ---- Lines tab ----------------------------------------------------------------------

function LinesTab({ services }: { readonly services: readonly SystemLoggersServiceDto[] }): ReactElement {
  const { t } = useTranslation();
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [tailBytesByService, setTailBytesByService] = useState<Record<string, number>>({});

  const queries = useQueries({
    queries: services.map((s) => {
      const tailBytes = tailBytesByService[s.service_id] ?? DEFAULT_TAIL_BYTES;
      return {
        queryKey: ['system', 'logfile', s.service_id, tailBytes] as const,
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiClient.getServiceLogTail(s.service_id, tailBytes, { signal }),
        refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL_MS : false,
        staleTime: 0,
      };
    }),
  });

  const expandTail = (serviceId: string): void => {
    setTailBytesByService((prev) => {
      const current = prev[serviceId] ?? DEFAULT_TAIL_BYTES;
      const next = Math.min(current * 2, MAX_TAIL_BYTES);
      return { ...prev, [serviceId]: next };
    });
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={autoRefresh}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setAutoRefresh(e.target.checked);
          }}
        />
        <span>{t('routes.admin.logs.lines.auto')}</span>
      </label>
      {services.map((s, idx) => {
        const q = queries[idx];
        const tailBytes = tailBytesByService[s.service_id] ?? DEFAULT_TAIL_BYTES;
        const tail = trimFirstLine(q?.data ?? '');
        return (
          <ServiceLogCard
            key={s.service_id}
            serviceId={s.service_id}
            tail={tail}
            tailBytes={tailBytes}
            onLoadMore={() => {
              expandTail(s.service_id);
            }}
            isFetching={q?.isFetching === true}
            isError={q?.isError === true}
          />
        );
      })}
    </div>
  );
}

function trimFirstLine(text: string): string {
  if (text === '') return '';
  const idx = text.indexOf('\n');
  // If the buffer starts mid-line (likely, because we read a byte-bounded suffix), skip
  // the partial first line. Keep the whole buffer if there's no newline yet.
  return idx === -1 ? text : text.substring(idx + 1);
}

interface ServiceLogCardProps {
  readonly serviceId: string;
  readonly tail: string;
  readonly tailBytes: number;
  readonly onLoadMore: () => void;
  readonly isFetching: boolean;
  readonly isError: boolean;
}

function ServiceLogCard({
  serviceId,
  tail,
  tailBytes,
  onLoadMore,
  isFetching,
  isError,
}: ServiceLogCardProps): ReactElement {
  const { t } = useTranslation();
  const canExpand = tailBytes < MAX_TAIL_BYTES;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-semibold tracking-tight">
            {serviceId.toLowerCase()}
          </CardTitle>
          <CardDescription className="mt-1 text-xs">{`${String(Math.round(tailBytes / 1024))} KiB`}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />}
          {canExpand && (
            <Button size="sm" variant="outline" onClick={onLoadMore} disabled={isFetching}>
              {t('routes.admin.logs.lines.loadMore')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isError ? (
          <p className="text-xs text-destructive">{t('routes.admin.logs.lines.errorTitle')}</p>
        ) : tail === '' ? (
          <p className="text-xs text-muted-foreground">{t('routes.admin.logs.lines.empty')}</p>
        ) : (
          <pre className="max-h-96 overflow-auto rounded bg-muted p-2 text-[11px] leading-snug whitespace-pre-wrap">
            {tail}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

// ---- skeletons ----------------------------------------------------------------------

function Skeletons(): ReactElement {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}
